<template>
  <transition name="fade">
    <div @mousedown="onDismiss" v-if="visible" :data-uuid="uuid" :class="[baseCss, modal ? backdropBlur ? 'bcq-dialog-box-backdrop-blur' : null : ['bcq-dialog-box-modal', 'pointer-events-none']]" :style="{zIndex}">
      <card
        @mousedown.native.stop
        role="dialog"
        tag="div"
        ref="card"
        :overflow-off="contentOverflowOff"
        :elevation="elevation"
        :bg-color="bgColor"
        :bg-color-lightness="bgColorLightness"
        :class="[fullScreen ? 'bcq-dialog-fullscreen' : null, 'pointer-events-auto', 'dialog-box-card', 'fixed', 'transition-all', 'duration-50']"
        :style="{maxWidth, minWidth}">
        <card-title
          ref="title"
          v-if="titleBar"
          :color="titleColor"
          :color-lightness="titleColorLightness"
          :bg-color="titleBgColor"
          :class="['select-none']"
          :bg-color-lightness="titleBgColorLightness">
          <table-layout equal-cells y-align="middle">
            <row>
              <cell v-if="icon" x-align="center" :class="['bcq-dialog-box-icon']">
                <icon
                  @dblclick.native.prevent="$emit('icon-dblclick')"
                  @click.native.prevent="$emit('icon-click')"
                  :provider="iconProvider"
                  :name="icon"
                  :size="titleIconSize"
                  :color="titleColor" />
              </cell>
              <cell>
                <div v-html="title" :class="['px-1', `text-${titleFontSize}`]" />
              </cell>
              <cell v-if="dismissButton" x-align="center" :class="['bcq-dialog-box-icon']">
                <icon
                  @click.native.stop="dismiss"
                  name="close"
                  :size="titleIconSize"
                  :provider="iconProvider"
                  :color="titleColor"
                  :color-lightness="titleColorLightness"
                  :class="['cursor-pointer']" />
              </cell>
            </row>
          </table-layout>
        </card-title>
        <template v-if="$slots.content">
          <divider v-if="titleDivider" />
          <card-text :class="['overflow-auto']" :padding-off="paddingOff" :style="{maxHeight: contentHeight}">
            <slot name="content" />
          </card-text>
        </template>
        <template v-if="$slots.actions">
          <divider v-if="actionsDivider" />
          <card-actions :align="actionsAlign">
            <slot name="actions" />
          </card-actions>
        </template>
      </card>
      <reactive-style v-if="fullScreen">
      .bcq-dialog-box[data-uuid="{{ uuid }}"] .bcq-dialog-fullscreen .bcq-card-text
      {
        height: calc(100vh - 37.77px {{ $slots.actions ? '- 53.27px' : '' }});
      }
      </reactive-style>
    </div>
  </transition>
</template>
<script>
import methods from '../../mixins/methods'
import props from '../../mixins/props'
import Utils from '../../mixins/utils'

export default {
  name: 'DialogBox',
  mixins: [
    props,
    methods
  ],
  data () {
    return {
      uuid: this.$uuidv4(),
      baseCss: this.componentBaseCss('dialog-box', ['fixed', 'top-0', 'left-0', 'w-screen', 'h-screen'])
    }
  },
  watch: {
    visible () {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.visible) {
            this.$el.querySelector('.bcq-card').classList.add('bcq-dialog-visible')
          }
        }, 4)
      })
    }
  },
  model: {
    prop: 'visible',
    event: 'dismiss'
  },
  props: {
    visible: {
      type: Boolean
    },
    titleBar: {
      type: Boolean,
      default: true
    },
    titleDivider: {
      type: Boolean,
      default: true
    },
    actionsDivider: {
      type: Boolean,
      default: true
    },
    zIndex: {
      type: Number,
      default: 15000
    },
    paddingOff: {
      type: Boolean
    },
    modal: {
      type: Boolean
    },
    backdropBlur: {
      type: Boolean
    },
    persistent: {
      type: Boolean
    },
    maxWidth: {
      type: String,
      default: '60%'
    },
    minWidth: {
      type: String,
      default: '300px'
    },
    title: {
      type: String
    },
    icon: {
      type: String
    },
    dismissButton: {
      type: Boolean,
      default: true
    },
    contentHeight: {
      type: String,
      default: 'auto'
    },
    bgColor: {
      type: String,
      default: 'white'
    },
    titleColor: {
      type: String,
      default: 'white'
    },
    titleIconSize: {
      type: Number,
      default: 1.5
    },
    contentOverflowOff: {
      type: Boolean,
      default: true
    },
    titleFontSize: {
      type: String,
      default: 'base',
      validator: value => {
        return [
          'xs',
          'sm',
          'md',
          'base',
          'lg',
          'xl',
          '2xl'
        ].includes(value)
      }
    },
    fullScreen: {
      type: Boolean
    },
    titleColorLightness: {
      type: [Number, String],
      default: null,
      validator: value => {
        const utils = new Utils()
        return utils.validateLightness(value)
      }
    },
    titleBgColor: {
      type: String,
      default: 'gray'
    },
    titleBgColorLightness: {
      type: [Number, String],
      default: 500,
      validator: value => {
        const utils = new Utils()
        return utils.validateLightness(value)
      }
    },
    actionsAlign: {
      type: String,
      default: 'right'
    }
  },
  methods: {
    dismiss (event) {
      this.$emit('dismiss', event.target.value)
    },
    onDismiss ($event) {
      if (!this.persistent) {
        this.$emit('dismiss')
      }
    }
  }
}
</script>
<style>
.bcq-dialog-box
{
  background: var(--semi-transparent-light);
  line-height: 100vh;
}

.bcq-ui-theme-dark .bcq-dialog-box
{
  background: var(--semi-transparent-dark);
}

.bcq-dialog-box-modal
{
  background: none !important;
}

.bcq-dialog-box .dialog-box-card
{
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
}

.bcq-dialog-box .dialog-box-card.bcq-dialog-visible
{
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.bcq-dialog-box .bcq-dialog-box-icon
{
  width: 32px;
}

.bcq-dialog-box:active .dialog-box-card.bcq-dialog-visible
{
  opacity: 1;
  transform: translate(-50%, -50%) scale(1.05);
}

.bcq-dialog-box .dialog-box-card.bcq-dialog-visible:active
{
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

.bcq-dialog-box .bcq-dialog-fullscreen
{
  top: 0 !important;
  left: 0 !important;
  transform: none !important;
  border-radius: 0 !important;
  width: 100vw !important;
  max-width: unset !important;
}

.bcq-dialog-box.bcq-dialog-box-backdrop-blur
{
  backdrop-filter: blur(4px);
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/dialog-box/dialog-box.vue